'use client';
/**
 * Who the current visitor is, shared by everything on the page.
 *
 * The answer arrives with the HTML: the locale layout resolves the session on the server and hands
 * it to `MeProvider`, so the first paint already shows the right name, badge and sign-in options,
 * with no round trip and no guess. `GET /api/me` is asked once after mount only to bring the
 * storage counters up to date, and again after anything that changes who you are.
 */
import * as React from 'react';
import type { MeResponse } from '@freealbum/shared';
import { api } from '@/lib/api';

/** Older builds cached the identity on the device to paper over the missing server answer. */
const LEGACY_CACHE_KEY = 'freealbum.me.v1';

export interface MeState {
  data: MeResponse;
  /** A refresh is in flight. What shows meanwhile is the last identity the server confirmed. */
  loading: boolean;
  error: unknown;
  refresh: () => Promise<MeResponse | null>;
}

const MeContext = React.createContext<MeState | null>(null);

export function MeProvider({ initial, children }: { initial: MeResponse; children: React.ReactNode }) {
  const [data, setData] = React.useState(initial);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  // A server re-render (router.refresh after signing in or out) is the newer truth.
  React.useEffect(() => { setData(initial); }, [initial]);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.me();
      setData(res);
      setError(null);
      return res;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    try { window.localStorage.removeItem(LEGACY_CACHE_KEY); } catch { /* private mode */ }
    void refresh();
  }, [refresh]);

  const value = React.useMemo<MeState>(() => ({ data, loading, error, refresh }), [data, loading, error, refresh]);
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeState {
  const state = React.useContext(MeContext);
  if (!state) throw new Error('useMe() needs <MeProvider>; the locale layout renders it with the identity the server resolved.');
  return state;
}
